import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

function InventoryRow({ item, onSave }) {
  const [draftQuantity, setDraftQuantity] = useState(String(item.quantity ?? 0));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraftQuantity(String(item.quantity ?? 0));
  }, [item.product_id, item.quantity]);

  const commitQuantity = useCallback(async () => {
    const normalizedQuantity = draftQuantity === '' ? 0 : parseInt(draftQuantity, 10);

    if (!Number.isFinite(normalizedQuantity) || normalizedQuantity < 0) {
      setDraftQuantity(String(item.quantity ?? 0));
      return;
    }

    try {
      setSaving(true);
      await onSave(item.product_id, normalizedQuantity);
      setDraftQuantity(String(normalizedQuantity));
    } catch (error) {
      setDraftQuantity(String(item.quantity ?? 0));
      console.error('Error updating inventory row:', error);
    } finally {
      setSaving(false);
    }
  }, [draftQuantity, item.product_id, item.quantity, onSave]);

  return (
    <tr>
      <td>{item.name}</td>
      <td>{item.purchase_price.toLocaleString()} ₫</td>
      <td>{item.retail_price_70k.toLocaleString()} ₫</td>
      <td>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            inputMode="numeric"
            value={draftQuantity}
            onChange={(e) => {
              if (/^\d*$/.test(e.target.value)) {
                setDraftQuantity(e.target.value);
              }
            }}
            onBlur={commitQuantity}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitQuantity();
              }
            }}
            style={{ width: '100px' }}
          />
          <button
            type="button"
            onClick={commitQuantity}
            disabled={saving}
          >
            {saving ? 'Đang lưu...' : 'Lưu'}
          </button>
        </div>
      </td>
    </tr>
  );
}

function Inventory() {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory');
      setInventory(response.data);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = useCallback(async (productId, quantity) => {
    try {
      await axios.put(`/api/inventory/${productId}`, { quantity });
      setInventory((prev) => prev.map((item) => (
        item.product_id === productId
          ? { ...item, quantity }
          : item
      )));
    } catch (error) {
      console.error('Error updating inventory:', error);
      alert('Lỗi khi cập nhật tồn kho');
      throw error;
    }
  }, []);

  const filteredInventory = inventory.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h2>Quản Lý Tồn Kho</h2>
          <p>Cập nhật số lượng tồn kho chi tiết theo sản phẩm.</p>
        </div>
        <input
          type="text"
          placeholder="Tìm kiếm sản phẩm..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '10px 14px', width: '280px', maxWidth: '100%' }}
        />
      </div>

      {loading ? (
        <div className="loading">Đang tải tồn kho...</div>
      ) : filteredInventory.length === 0 ? (
        <div className="empty-state">Không tìm thấy sản phẩm</div>
      ) : (
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>Sản phẩm</th>
                <th>Giá nhập</th>
                <th>Giá bán lẻ</th>
                <th>Tồn kho</th>
              </tr>
            </thead>
            <tbody>
              {filteredInventory.map((item) => (
                <InventoryRow
                  key={item.product_id}
                  item={item}
                  onSave={updateQuantity}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default Inventory;
